import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import type {AssistantInterestContext} from '@prompt-bridge/shared/ai-assistant-core';

import type {ToSalesInterestAssistantContextInput} from '../types/ToSalesInterestAssistantContextInput';
import {toAssistantInterestExcerpt} from './toAssistantInterestExcerpt';
import {toBoundedAssistantInterestText} from './toBoundedAssistantInterestText';

const toBoundedUtm = (
  input: ToSalesInterestAssistantContextInput
): NonNullable<AssistantInterestContext['origin']>['utm'] => {
  const utm = {
    source: toBoundedAssistantInterestText(input.interest.utm?.source),
    medium: toBoundedAssistantInterestText(input.interest.utm?.medium),
    campaign: toBoundedAssistantInterestText(input.interest.utm?.campaign),
    term: toBoundedAssistantInterestText(input.interest.utm?.term),
    content: toBoundedAssistantInterestText(input.interest.utm?.content),
  };

  return match(Object.values(utm).some(isNotNil))
    .with(true, () => ({
      ...(isNotNil(utm.source) ? {source: utm.source} : {}),
      ...(isNotNil(utm.medium) ? {medium: utm.medium} : {}),
      ...(isNotNil(utm.campaign) ? {campaign: utm.campaign} : {}),
      ...(isNotNil(utm.term) ? {term: utm.term} : {}),
      ...(isNotNil(utm.content) ? {content: utm.content} : {}),
    }))
    .otherwise(() => undefined);
};

export const toSalesInterestOrigin = (
  input: ToSalesInterestAssistantContextInput
): AssistantInterestContext['origin'] => {
  const sourceCodeId = toBoundedAssistantInterestText(input.interest.sourceCodeId);
  const sourceLabel = match(input.sourceCode)
    .with({isDisabled: false}, ({name}) => toBoundedAssistantInterestText(name))
    .otherwise(() => null);
  const utm = toBoundedUtm(input);
  const originalInquiry = match(input.originLeadMessage?.body)
    .with(Pattern.string, toAssistantInterestExcerpt)
    .otherwise(() => null);
  const origin = {
    ...(isNotNil(sourceCodeId) ? {sourceCodeId} : {}),
    ...(isNotNil(sourceLabel) ? {sourceLabel} : {}),
    ...(isNotNil(utm) ? {utm} : {}),
    ...(isNotNil(originalInquiry)
      ? {
          originalInquiryExcerpt: originalInquiry.text,
          originalInquiryExcerptTruncated: originalInquiry.truncated,
        }
      : {}),
  };

  return match(Object.keys(origin).length > 0)
    .with(true, () => origin)
    .otherwise(() => undefined);
};
